/**
 * Base skeleton component for loading states
 */

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

export function Skeleton({
  className = '',
  variant = 'rectangular',
  width,
  height,
  animation = 'pulse',
}: SkeletonProps) {
  const baseClasses = 'bg-gradient-to-r from-[#E2E8F0] via-[#EDF2F7] to-[#E2E8F0] bg-[length:200%_100%]';

  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer',
    none: '',
  };

  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${animationClasses[animation]} ${className}`}
      style={style}
    />
  );
}

/**
 * Skeleton for a single line of text
 */
export function SkeletonText({ className = '', width = '100%' }: { className?: string; width?: string | number }) {
  return <Skeleton variant="text" height={16} width={width} className={className} />;
}

/**
 * Skeleton for a heading
 */
export function SkeletonHeading({ className = '', level = 1 }: { className?: string; level?: 1 | 2 | 3 }) {
  const heights = { 1: 32, 2: 24, 3: 20 };
  return <Skeleton variant="text" height={heights[level]} width="40%" className={className} />;
}

/**
 * Skeleton for a stat card
 */
export function SkeletonStatCard() {
  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] p-6">
      <Skeleton height={16} width="60%" className="mb-3" />
      <Skeleton height={36} width="40%" className="mb-2" />
      <Skeleton height={14} width="50%" />
    </div>
  );
}

/**
 * Skeleton for a table row
 */
export function SkeletonTableRow({ columns = 5 }: { columns?: number }) {
  return (
    <tr className="border-b border-[#E2E8F0]">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton height={16} width={i === 0 ? '80%' : '60%'} />
        </td>
      ))}
    </tr>
  );
}

/**
 * Skeleton for a document card
 */
export function SkeletonDocumentCard() {
  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] p-4">
      <Skeleton height={120} className="mb-3" />
      <Skeleton height={16} width="70%" className="mb-2" />
      <Skeleton height={14} width="50%" />
    </div>
  );
}

/**
 * Skeleton for an account/list item card
 */
export function SkeletonListCard() {
  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <Skeleton height={20} width="50%" className="mb-2" />
          <Skeleton height={14} width="70%" />
        </div>
        <Skeleton variant="circular" width={32} height={32} />
      </div>
      <div className="flex gap-4 mt-4">
        <Skeleton height={14} width="30%" />
        <Skeleton height={14} width="30%" />
      </div>
    </div>
  );
}
